import { createApi } from '@reduxjs/toolkit/query/react'
import { Patient } from '../types'
import { axiosBaseQuery } from './baseQuery'
import { generatePaginationQuery } from '../utils/service/service-helper'
import {
  BatchCreatePatientsResponseItem,
  CreatePatientRequest,
  emptyPatient,
  transformPatientRequestPayload,
} from '../utils/patient/patientPayload'

type PatientsQueryObj = {
  prescriberId?: string
  page?: number
  limit?: number
  searchTerm?: string
  locationId?: string
}

type CreatePatientQueryObj = {
  prescriberId?: string
  patient?: CreatePatientRequest
}

type BatchCreatePatientsQueryObj = {
  prescriberId?: string
  patients?: CreatePatientRequest[]
}

type UpdatePatientQueryObj = {
  prescriberId?: string
  patient: Patient
  patientId?: string
}

type GetPatientByIdQueryObj = {
  patientId: string
}

// Define a service using a base URL and expected endpoints
export const patientsApi = createApi({
  reducerPath: 'patientsApi',
  tagTypes: ['Patients'],
  baseQuery: axiosBaseQuery({
    baseUrl: '',
  }),
  endpoints: (build) => ({
    getPatientById: build.query<Patient, GetPatientByIdQueryObj>({
      query: ({ patientId }) => ({
        url: `/bff/patients/${patientId}`,
        method: 'get',
      }),
      providesTags: (_result, _error, { patientId }) => [{ type: 'Patients', id: patientId }],
    }),
    getPatients: build.query<
      { patients: Patient[]; meta: { totalPages: number; totalItems: number } },
      PatientsQueryObj
    >({
      query: ({ page, limit, searchTerm, locationId, ...scopeData }) => {
        const prescriberId = scopeData.prescriberId
        const queryStr = generatePaginationQuery(
          limit,
          page,
          searchTerm,
          prescriberId,
          locationId
        ).toString()
        return {
          url: `/bff/patients?${queryStr}`,
          method: 'get',
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.patients.map(({ id }) => ({ type: 'Patients' as const, id })),
              { type: 'Patients', id: 'PARTIAL-LIST' },
            ]
          : [{ type: 'Patients', id: 'PARTIAL-LIST' }],
    }),
    createPatient: build.mutation<Patient, CreatePatientQueryObj>({
      query: ({ patient, ...scopeData }) => {
        if (!patient) {
          throw new Error('New patient is required to create patient.')
        }

        const prescriberId = scopeData.prescriberId

        return {
          url: `/bff/patients`,
          method: 'post',
          data: {
            patient,
            prescriberId,
          },
        }
      },
      invalidatesTags: (_result, error) => (error ? [] : ['Patients']),
    }),
    batchCreatePatients: build.mutation<
      BatchCreatePatientsResponseItem[],
      BatchCreatePatientsQueryObj
    >({
      query: ({ patients, ...scopeData }) => {
        if (!patients?.length) {
          throw new Error('Patients data are required to batch create patients.')
        }

        const prescriberId = scopeData.prescriberId
        const formattedPatients = patients.map((patient) => {
          const fullPatientData = { ...emptyPatient, ...patient }
          return transformPatientRequestPayload<CreatePatientRequest>(fullPatientData)
        })
        return {
          url: `/bff/patients/batch`,
          method: 'post',
          data: {
            patients: formattedPatients,
            prescriberId: prescriberId,
          },
        }
      },
      invalidatesTags: (_result, error) => (error ? [] : ['Patients']),
    }),
    deletePatient: build.mutation<null, { patientId?: string; prescriberId?: string }>({
      query: ({ patientId, ...scopeData }) => {
        if (!patientId) {
          throw new Error('Patient id is required to delete patient.')
        }

        const prescriberId = scopeData.prescriberId
        const url = prescriberId
          ? `/bff/patients/${patientId}?prescriberId=${prescriberId}`
          : `/bff/patients/${patientId}`

        return {
          url,
          method: 'delete',
        }
      },
      invalidatesTags: (_result, error, { patientId }) =>
        error
          ? []
          : [
              { type: 'Patients', id: patientId },
              { type: 'Patients', id: 'PARTIAL-LIST' },
            ],
    }),
    updatePatient: build.mutation<Patient, UpdatePatientQueryObj>({
      query: ({ patient, patientId, ...scopeData }) => {
        if (!patientId) {
          throw new Error('Patient id is required to update patient.')
        }

        const prescriberId = scopeData.prescriberId

        return {
          url: `/bff/patients/${patientId}`,
          method: 'patch',
          data: {
            patient,
            prescriberId,
          },
        }
      },
      invalidatesTags: (_result, error, { patientId }) =>
        error ? [] : [{ type: 'Patients', id: patientId }],
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useUpdatePatientMutation,
  useDeletePatientMutation,
  useCreatePatientMutation,
  useBatchCreatePatientsMutation,
  useLazyGetPatientByIdQuery,
  useLazyGetPatientsQuery,
  useGetPatientsQuery,
} = patientsApi
